import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X, Send, Image as ImageIcon, Camera, Mic, Trash2, StopCircle, Loader2,
  Check, CheckCheck, Clock3, AlertCircle, ChevronDown, ChevronUp, Package,
} from "lucide-react";
import { toast } from "sonner";
import http from "@/api";
import { useApp } from "@/context";
import AudioPlayer from "@/components/AudioPlayer";
import Chatbot from "@/components/Chatbot";

const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;
const mediaSrc = (u) => (u && u.startsWith("/") ? `${API_ORIGIN}${u}` : u);
const GREEN = "#0f7a4d";

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const dayKey = (iso) => (iso || "").slice(0, 10);
const fmtDay = (iso, ar) => {
  try {
    const d = new Date(iso);
    const today = new Date(); const yest = new Date(Date.now() - 864e5);
    if (d.toDateString() === today.toDateString()) return ar ? "اليوم" : "Today";
    if (d.toDateString() === yest.toDateString()) return ar ? "أمس" : "Yesterday";
    return d.toLocaleDateString(ar ? "ar" : "en-GB", { day: "numeric", month: "long" });
  } catch { return ""; }
};

const linkify = (text) => {
  const parts = String(text).split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) => {
    if (!/^https?:\/\//.test(p)) return p;
    const m = p.match(/^(.*?)([.،,;:!؟)»"']*)$/);
    const url = m ? m[1] : p;
    const tail = m ? m[2] : "";
    return (
      <React.Fragment key={i}>
        <a href={url} target="_blank" rel="noopener noreferrer"
           className="underline font-semibold" style={{ color: GREEN }}>
          {url.replace(/^https?:\/\//, "")}
        </a>{tail}
      </React.Fragment>
    );
  });
};

// ضغط الصور قبل الرفع (حد أقصى 1600px، JPEG 85%) — يسرّع الإرسال دون فقد ملحوظ
const compressImage = (file) => new Promise((resolve) => {
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.size < 300 * 1024) return resolve(file);
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const MAX = 1600;
    let { width: w, height: h } = img;
    if (w > MAX || h > MAX) { const r = Math.min(MAX / w, MAX / h); w = Math.round(w * r); h = Math.round(h * r); }
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(img, 0, 0, w, h);
    c.toBlob((b) => {
      URL.revokeObjectURL(url);
      resolve(b && b.size < file.size
        ? new File([b], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" })
        : file);
    }, "image/jpeg", 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

const OrderCard = ({ o, ar, onOpen }) => (
  <button onClick={onOpen} className="flex items-center gap-2.5 rounded-xl p-2 w-full text-start"
          style={{ background: "#f3fbf6", border: `1px solid ${GREEN}33` }} data-testid="cs-order-card">
    {o.item_image
      ? <img src={o.item_image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
      : <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#e2f3e9" }}>
          <Package size={20} style={{ color: GREEN }} /></div>}
    <div className="min-w-0 flex-1">
      <div className="text-[11px] font-black" style={{ color: GREEN }} dir="ltr">📦 {o.code}</div>
      <div className="text-[11px] font-semibold truncate">{ar ? o.item_name_ar || o.item_name_en : o.item_name_en || o.item_name_ar}
        {o.items_count > 1 && <span className="opacity-50"> +{o.items_count - 1}</span>}
      </div>
      <div className="text-[10px] opacity-60" dir="ltr">{Number(o.total || 0).toFixed(0)} {o.currency}</div>
    </div>
  </button>
);

export default function CustomerServiceWidget({ open, onClose, onRead, orderId }) {
  const { user, lang } = useApp();
  const ar = lang === "ar";
  const nav = useNavigate();
  const [tab, setTab] = useState("ai");
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState([]); // {id,file,preview,pct,status}
  const [sendingMedia, setSendingMedia] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [kb, setKb] = useState(0);
  const [online, setOnline] = useState(null);
  const [quick, setQuick] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const scrollRef = useRef(null);
  const taRef = useRef(null);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recSecsRef = useRef(0);
  const cancelledRef = useRef(false);
  const linkedOrderRef = useRef("");
  const lastIdRef = useRef("");

  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const draftKey = conv?.id ? `rg_cs_draft_${conv.id}` : "rg_cs_draft";

  // ---- helpers ----------------------------------------------------------- //
  const nearBottom = () => {
    const el = scrollRef.current;
    return el ? el.scrollHeight - el.scrollTop - el.clientHeight < 140 : true;
  };
  const scrollToBottom = (smooth = false) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  // زر «تواصل بشأن الطلب» يفتح مباشرة على تبويب الفريق مع بطاقة الطلب
  useEffect(() => {
    if (open && orderId) setTab("team");
  }, [open, orderId]);

  // عند الإغلاق: صفّر مؤشر آخر رسالة حتى يعيد الفتح التمرير للأسفل
  useEffect(() => {
    if (!open) { lastIdRef.current = ""; setNewCount(0); }
  }, [open]);

  // ---- data -------------------------------------------------------------- //
  const fetchMessages = useCallback(async (cid, { initial = false } = {}) => {
    if (!cid) return;
    try {
      const { data } = await http.get(`/support/conversations/${cid}/messages?limit=40`);
      const real = data.messages || [];
      const wasNear = nearBottom();
      const prevLastId = lastIdRef.current;
      setHasMore((h) => (initial ? !!data.has_more : h));
      setMessages((prev) => {
        const unconfirmed = prev.filter((m) => (m.pending || m.failed)
          && !real.some((r) => r.client_msg_id && r.client_msg_id === m.client_msg_id));
        const older = initial ? [] : prev.filter((m) => !m.pending && !m.failed
          && real.length && m.created_at < real[0].created_at);
        return [...older, ...real, ...unconfirmed];
      });
      const last = real[real.length - 1];
      if (initial) {
        if (last) lastIdRef.current = last.id;
        requestAnimationFrame(() => scrollToBottom(false));
        setNewCount(0);
      } else if (last && last.id !== prevLastId) {
        lastIdRef.current = last.id;
        const incoming = last.sender_role !== "customer";
        if (wasNear || !incoming) {
          requestAnimationFrame(() => scrollToBottom(true));
          setNewCount(0);
        } else if (incoming) {
          setNewCount((c) => c + 1);
        }
      }
      onRead?.();
    } catch (_) { /* شبكة ضعيفة — المحاولة القادمة خلال ثوانٍ */ }
  }, [onRead]);

  const loadOlder = async () => {
    if (!conv?.id || !messages.length || loadingOlder) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const beforeH = el?.scrollHeight || 0;
    try {
      const oldest = messages.find((m) => !m.pending && !m.failed);
      const { data } = await http.get(
        `/support/conversations/${conv.id}/messages?limit=40&before=${encodeURIComponent(oldest?.created_at || "")}`);
      const older = data.messages || [];
      setHasMore(!!data.has_more);
      if (older.length) {
        setMessages((prev) => [...older, ...prev]);
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - beforeH;
        });
      }
    } catch (_) { /* */ }
    setLoadingOlder(false);
  };

  // فتح المحادثة + ربط الطلب إن وُجد
  useEffect(() => {
    if (!open || !user || tab !== "team") return;
    let cancelled = false;
    (async () => {
      try {
        let c = conv;
        const needLink = orderId && linkedOrderRef.current !== orderId;
        if (!c || needLink) {
          const { data } = await http.post("/support/conversations",
            needLink ? { order_id: orderId } : {});
          if (cancelled) return;
          if (needLink) linkedOrderRef.current = orderId;
          c = data;
          setConv(data);
        }
        await fetchMessages(c.id, { initial: true });
      } catch (_) { /* */ }
    })();
    const t = setInterval(() => conv?.id && fetchMessages(conv.id), 3000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, tab, conv?.id, orderId]);

  // حالة الفريق + الردود السريعة
  useEffect(() => {
    if (!open) return;
    const load = () => http.get("/support/status").then((r) => setOnline(!!r.data.online)).catch(() => {});
    load();
    http.get("/support/quick-replies").then((r) => setQuick(r.data.replies || [])).catch(() => {});
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [open]);

  // المسودة: استرجاع عند الفتح وحفظ أثناء الكتابة
  useEffect(() => {
    if (open && conv?.id) {
      const d = localStorage.getItem(draftKey);
      if (d && !input) setInput(d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, conv?.id]);
  const onInputChange = (v) => {
    setInput(v);
    try { v ? localStorage.setItem(draftKey, v) : localStorage.removeItem(draftKey); } catch (_) { /* */ }
    const el = taRef.current;
    if (el) { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 120) + "px"; }
  };

  // لوحة المفاتيح (iOS visual viewport)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!open || !vv) return;
    const onResize = () => {
      const off = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKb(off > 60 ? off : 0);
      if (off > 60 && nearBottom()) requestAnimationFrame(() => scrollToBottom());
    };
    onResize();
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => { vv.removeEventListener("resize", onResize); vv.removeEventListener("scroll", onResize); };
  }, [open]);

  // ---- send -------------------------------------------------------------- //
  const postText = async (text, clientId) => {
    await http.post(`/support/conversations/${conv.id}/messages`, { text, client_msg_id: clientId });
  };

  const sendMessage = async (text) => {
    if (!text || !conv) return;
    const clientId = `c${Date.now()}${Math.random().toString(36).slice(2, 8)}`;
    setMessages((m) => [...m, {
      id: `tmp-${clientId}`, client_msg_id: clientId, sender_role: "customer", text,
      created_at: new Date().toISOString(), pending: true,
    }]);
    requestAnimationFrame(() => scrollToBottom(true));
    try {
      await postText(text, clientId);
      try { localStorage.removeItem(draftKey); } catch (_) { /* */ }
      fetchMessages(conv.id);
    } catch (e) {
      setMessages((m) => m.map((x) => x.client_msg_id === clientId
        ? { ...x, pending: false, failed: true } : x));
    }
  };

  const retryMessage = async (m) => {
    setMessages((prev) => prev.map((x) => x.client_msg_id === m.client_msg_id
      ? { ...x, failed: false, pending: true } : x));
    try {
      await postText(m.text, m.client_msg_id);
      fetchMessages(conv.id);
    } catch (_) {
      setMessages((prev) => prev.map((x) => x.client_msg_id === m.client_msg_id
        ? { ...x, pending: false, failed: true } : x));
    }
  };

  const sendText = () => {
    const text = input.trim();
    if (attachments.length) { sendAttachments(text); return; }
    if (!text) return;
    setInput("");
    if (taRef.current) taRef.current.style.height = "auto";
    sendMessage(text);
  };

  // ---- attachments ------------------------------------------------------- //
  const addFiles = async (list) => {
    const files = Array.from(list || []).slice(0, 6 - attachments.length);
    for (const f of files) {
      const isImg = f.type.startsWith("image/");
      const isPdf = f.type === "application/pdf";
      if (!isImg && !isPdf) { toast.error(ar ? "صور أو PDF فقط" : "Images or PDF only"); continue; }
      if (f.size > 15 * 1024 * 1024) { toast.error(ar ? "الحد الأقصى 15MB" : "Max 15MB"); continue; }
      const file = isImg ? await compressImage(f) : f;
      if (isImg && file.size > 8 * 1024 * 1024) { toast.error(ar ? "الصورة كبيرة جداً (حد 8MB)" : "Image too large (8MB max)"); continue; }
      setAttachments((a) => [...a, {
        id: `a${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
        file, name: file.name || f.name, isPdf,
        preview: isImg ? URL.createObjectURL(file) : "",
        pct: 0, status: "ready",
      }]);
    }
  };
  const pickFiles = (e) => { addFiles(e.target.files); e.target.value = ""; };
  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const sendAttachments = async (caption = "") => {
    if (!conv || !attachments.length || sendingMedia) return;
    setSendingMedia(true);
    const items = [...attachments];
    for (const it of items) {
      try {
        setAttachments((a) => a.map((x) => x.id === it.id ? { ...x, status: "uploading" } : x));
        const fd = new FormData();
        fd.append("file", it.file, it.name || it.file.name || "photo.jpg");
        fd.append("duration_sec", "0");
        await http.post(`/support/conversations/${conv.id}/media`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
            setAttachments((a) => a.map((x) => x.id === it.id ? { ...x, pct } : x));
          },
        });
        setAttachments((a) => a.filter((x) => x.id !== it.id));
      } catch (e) {
        setAttachments((a) => a.map((x) => x.id === it.id ? { ...x, status: "failed" } : x));
        toast.error(e?.response?.data?.detail || (ar ? "تعذّر رفع الملف — أعد المحاولة" : "Upload failed"));
        break;
      }
    }
    setSendingMedia(false);
    await fetchMessages(conv.id);
    requestAnimationFrame(() => scrollToBottom(true));
    const text = caption.trim();
    if (text) { setInput(""); sendMessage(text); }
  };

  // ---- voice (كما هو) ---------------------------------------------------- //
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      cancelledRef.current = false;
      rec.ondataavailable = (ev) => { if (ev.data.size) chunksRef.current.push(ev.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (recTimerRef.current) clearInterval(recTimerRef.current);
        const secs = recSecsRef.current;
        setRecording(false); setRecSecs(0);
        if (cancelledRef.current || !chunksRef.current.length) return;
        const type = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes("mp4") ? "m4a" : "webm";
        (async () => {
          setSendingMedia(true);
          try {
            const fd = new FormData();
            fd.append("file", blob, `voice.${ext}`);
            fd.append("duration_sec", String(secs));
            await http.post(`/support/conversations/${conv.id}/media`, fd,
              { headers: { "Content-Type": "multipart/form-data" } });
            await fetchMessages(conv.id);
            requestAnimationFrame(() => scrollToBottom(true));
          } catch (e) {
            toast.error(ar ? "تعذّر إرسال التسجيل" : "Voice upload failed");
          } finally { setSendingMedia(false); }
        })();
      };
      recRef.current = rec;
      rec.start();
      setRecording(true); setRecSecs(0);
      recSecsRef.current = 0;
      recTimerRef.current = setInterval(() => {
        recSecsRef.current += 1;
        setRecSecs((s) => s + 1);
      }, 1000);
    } catch (e) {
      toast.error(ar ? "فعّل إذن المايكروفون من المتصفح" : "Allow microphone access");
    }
  };
  const stopRecording = (cancel = false) => {
    cancelledRef.current = cancel;
    try { recRef.current?.stop(); } catch (_) { /* */ }
  };

  // ---- derived ----------------------------------------------------------- //
  const grouped = useMemo(() => {
    const out = [];
    let lastDay = "";
    for (const m of messages) {
      const d = dayKey(m.created_at);
      if (d && d !== lastDay) { out.push({ _sep: true, id: `sep-${d}`, day: m.created_at }); lastDay = d; }
      out.push(m);
    }
    return out;
  }, [messages]);

  if (!open) return null;

  const vvH = window.visualViewport?.height || window.innerHeight;
  const panelHeight = isMobile ? `${kb ? vvH : window.innerHeight}px` : "min(80vh, 720px)";

  return (
    <div className={isMobile ? "fixed z-50 inset-0" : "fixed z-50 sm:end-4 sm:bottom-[80px] sm:w-[440px]"}
         data-testid="cs-widget">
      <div className="sm:rounded-2xl overflow-hidden shadow-2xl flex flex-col bg-white"
           style={{ height: panelHeight, border: "1px solid rgba(0,0,0,0.10)" }}>
        {/* Header */}
        <div className="flex items-center gap-2.5 px-3 shrink-0"
             style={{ background: "#11392A", paddingTop: isMobile && !kb ? "max(8px, env(safe-area-inset-top))" : 8, paddingBottom: 8 }}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-lg shrink-0"
               style={{ background: "rgba(255,255,255,0.12)" }}>🌸</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white" style={{ letterSpacing: "0.06em" }}>RARE GROUP</div>
            <div className="text-[10px] text-white/80 flex items-center gap-1" data-testid="cs-online-status">
              <span className={`w-1.5 h-1.5 rounded-full inline-block ${online ? "bg-emerald-400" : "bg-amber-300"}`} />
              {online === null
                ? (ar ? "مساعد ذكي فوري + فريقنا 🌸" : "AI + our team 🌸")
                : online
                  ? (ar ? "الفريق متصل الآن ✓" : "Team online now ✓")
                  : (ar ? "سنرد عليك بأقرب وقت — المساعد الذكي فوري" : "We'll reply soon — AI is instant")}
            </div>
          </div>
          {conv?.order_code && (
            <span className="text-[10px] font-black px-2 py-1 rounded-full" dir="ltr"
                  style={{ background: "rgba(255,255,255,0.14)", color: "#fff" }} data-testid="cs-order-chip">
              📦 {conv.order_code}
            </span>
          )}
          <button onClick={onClose} className="p-2 text-white/80 hover:text-white" data-testid="cs-close">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex shrink-0" style={{ background: "#f6f3ec", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
          {[["ai", ar ? "🤖 المساعد الذكي" : "🤖 AI assistant"],
            ["team", ar ? "👥 فريق خدمة العملاء" : "👥 Support team"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
                    className="flex-1 py-2.5 text-xs font-bold transition-colors"
                    style={tab === k
                      ? { background: "#fff", color: GREEN, borderBottom: `2px solid ${GREEN}` }
                      : { color: "rgba(0,0,0,0.45)" }}
                    data-testid={`cs-tab-${k}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "ai" && <Chatbot embedded onClose={onClose} onHandoff={() => setTab("team")} />}

        {tab === "team" && (!user ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center"
               style={{ background: "#efe9df" }}>
            <div className="text-3xl">💬</div>
            <div className="text-sm font-semibold">{ar ? "سجّل الدخول لتتواصل معنا" : "Log in to chat with us"}</div>
            <div className="text-xs opacity-60">{ar ? "ثوانٍ وتكون داخل — برقم واتسابك" : "Takes seconds with your WhatsApp number"}</div>
            <button onClick={() => { onClose(); nav("/login"); }} className="btn-primary !h-9 !text-sm"
                    data-testid="cs-login-btn">
              {ar ? "تسجيل الدخول" : "Log in"}
            </button>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 overscroll-contain relative"
                 style={{ background: "#efe9df" }} data-testid="cs-messages"
                 onScroll={() => { if (nearBottom()) setNewCount(0); }}>
              {hasMore && (
                <div className="text-center">
                  <button onClick={loadOlder} disabled={loadingOlder}
                          className="text-[11px] font-bold px-3 py-1.5 rounded-full bg-white shadow-sm inline-flex items-center gap-1"
                          data-testid="cs-load-older">
                    {loadingOlder ? <Loader2 size={11} className="animate-spin" /> : <ChevronUp size={11} />}
                    {ar ? "عرض الرسائل الأقدم" : "Load older messages"}
                  </button>
                </div>
              )}
              <Bubble mine={false} ar={ar}>
                <div className="text-[10px] font-semibold mb-0.5" style={{ color: "#8a6c33" }}>
                  {ar ? "فريق RARE GROUP 🌸" : "RARE GROUP team 🌸"}
                </div>
                <div className="text-sm">{ar
                  ? "أهلاً بك في RARE GROUP 🌸 كيف نقدر نخدمك؟"
                  : "Welcome to RARE GROUP 🌸 How can we help?"}</div>
              </Bubble>
              {!messages.some((m) => m.sender_role === "customer" && m.type !== "order") && quick.length > 0 && (
                <div className="flex flex-wrap gap-1.5 justify-end pt-1" data-testid="cs-quick-questions">
                  {quick.map((q, i) => (
                    <button key={i} onClick={() => sendMessage(ar ? q.ar : q.en)}
                            className="text-xs rounded-full px-3 py-1.5 bg-white shadow-sm font-medium hover:shadow transition-shadow"
                            style={{ border: "1px solid rgba(0,0,0,0.12)" }}
                            data-testid={`cs-quick-${i}`}>
                      {ar ? q.ar : q.en}
                    </button>
                  ))}
                </div>
              )}
              {grouped.map((m) => {
                if (m._sep) return (
                  <div key={m.id} className="flex justify-center py-1">
                    <span className="text-[10px] font-bold px-3 py-1 rounded-full bg-white/80 shadow-sm opacity-70">
                      {fmtDay(m.day, ar)}
                    </span>
                  </div>
                );
                const mine = m.sender_role === "customer";
                return (
                  <Bubble key={m.id} mine={mine} ar={ar}>
                    {!mine && (
                      <div className="text-[10px] font-semibold mb-0.5" style={{ color: "#8a6c33" }}>
                        {m.is_ai
                          ? (ar ? "مساعد RARE GROUP 🤖" : "RARE GROUP assistant 🤖")
                          : (m.sender_name
                              ? `${m.sender_name} — ${ar ? "خدمة العملاء 🌸" : "Support 🌸"}`
                              : (ar ? "فريق RARE GROUP 🌸" : "RARE GROUP team 🌸"))}
                      </div>
                    )}
                    {m.type === "order" && m.order && (
                      <OrderCard o={m.order} ar={ar}
                                 onOpen={() => { onClose(); nav(`/orders`); }} />
                    )}
                    {m.media_url && m.type === "image" && (
                      <img src={mediaSrc(m.media_url)} alt="" loading="lazy"
                           onClick={() => setZoom(mediaSrc(m.media_url))}
                           onLoad={() => {
                             const el = scrollRef.current;
                             if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 420) scrollToBottom();
                           }}
                           className="rounded-lg max-w-[230px] max-h-[260px] object-cover cursor-zoom-in"
                           data-testid={`cs-img-${m.id}`} />
                    )}
                    {m.media_url && (m.type === "voice" || m.type === "audio") && (
                      <AudioPlayer src={mediaSrc(m.media_url)} durationHint={m.duration_sec || 0}
                                   accent={GREEN} testId={`cs-voice-${m.id}`}
                                   fileName={`voice-${(m.created_at || "").slice(0, 10) || "message"}`} />
                    )}
                    {m.media_url && m.type === "document" && (
                      <a href={mediaSrc(m.media_url)} target="_blank" rel="noreferrer"
                         className="flex items-center gap-1 text-xs font-bold underline"
                         style={{ color: GREEN }} data-testid={`cs-doc-${m.id}`}>
                        📄 {m.media_filename || (ar ? "ملف مرفق" : "Attached file")}
                      </a>
                    )}
                    {m.text && <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">{linkify(m.text)}</div>}
                    <div className={`text-[9px] opacity-50 mt-0.5 flex items-center gap-1 ${mine ? "justify-end" : ""}`} dir="ltr">
                      {fmtTime(m.created_at)}
                      {mine && (m.failed
                        ? <AlertCircle size={11} className="text-red-500" />
                        : m.pending
                          ? <Clock3 size={10} />
                          : m.read_by_admin_at
                            ? <CheckCheck size={12} className="text-sky-600" />
                            : <Check size={11} className="opacity-70" />)}
                    </div>
                    {m.failed && (
                      <button onClick={() => retryMessage(m)}
                              className="text-[10px] font-bold text-red-600 underline mt-0.5"
                              data-testid={`cs-retry-${m.client_msg_id}`}>
                        {ar ? "فشل الإرسال — اضغط لإعادة المحاولة" : "Failed — tap to retry"}
                      </button>
                    )}
                  </Bubble>
                );
              })}
            </div>

            {newCount > 0 && (
              <button onClick={() => { scrollToBottom(true); setNewCount(0); }}
                      className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1 text-[11px] font-bold text-white px-3 py-1.5 rounded-full shadow-lg"
                      style={{ background: GREEN, bottom: 120, zIndex: 5 }} data-testid="cs-new-msgs-pill">
                <ChevronDown size={12} /> {ar ? `${newCount} رسالة جديدة` : `${newCount} new`}
              </button>
            )}

            {/* معاينة المرفقات قبل الإرسال */}
            {attachments.length > 0 && (
              <div className="flex gap-2 px-3 py-2 overflow-x-auto shrink-0"
                   style={{ background: "#f6f3ec", borderTop: "1px solid rgba(0,0,0,0.06)" }}
                   data-testid="cs-attach-preview">
                {attachments.map((a) => (
                  <div key={a.id} className="relative shrink-0">
                    {a.preview
                      ? <img src={a.preview} alt="" className="w-14 h-14 rounded-lg object-cover" />
                      : <div className="w-14 h-14 rounded-lg flex items-center justify-center text-lg" style={{ background: "#fde68a" }}>📄</div>}
                    {a.status === "uploading" && (
                      <div className="absolute inset-0 rounded-lg bg-black/45 flex items-center justify-center text-[10px] text-white font-black">
                        {a.pct}%
                      </div>
                    )}
                    {a.status === "failed" && (
                      <div className="absolute inset-0 rounded-lg bg-red-500/50 flex items-center justify-center text-white">
                        <AlertCircle size={16} />
                      </div>
                    )}
                    {a.status !== "uploading" && (
                      <button onClick={() => removeAttachment(a.id)}
                              className="absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full bg-black text-white flex items-center justify-center"
                              data-testid={`cs-attach-remove-${a.id}`}>
                        <X size={11} />
                      </button>
                    )}
                  </div>
                ))}
                <button onClick={() => sendAttachments(input)} disabled={sendingMedia}
                        className="self-center shrink-0 px-3 py-2 rounded-full text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                        style={{ background: GREEN }} data-testid="cs-attach-send">
                  {sendingMedia ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} className={ar ? "rtl-flip" : ""} />}
                  {ar ? "إرسال" : "Send"}
                </button>
              </div>
            )}

            {/* Composer */}
            <div className="flex items-end gap-1 p-2 shrink-0"
                 style={{ background: "#f6f3ec", borderTop: "1px solid rgba(0,0,0,0.06)",
                          paddingBottom: kb ? 8 : "max(8px, env(safe-area-inset-bottom))" }}>
              {recording ? (
                <div className="flex-1 flex items-center gap-2 px-2" data-testid="cs-recording-bar">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-sm font-mono tabular-nums" dir="ltr">
                    {String(Math.floor(recSecs / 60))}:{String(recSecs % 60).padStart(2, "0")}
                  </span>
                  <span className="text-xs opacity-60">{ar ? "جاري التسجيل…" : "Recording…"}</span>
                  <button onClick={() => stopRecording(true)} className="ms-auto p-2 text-red-500" title={ar ? "إلغاء" : "Cancel"}
                          data-testid="cs-rec-cancel"><Trash2 size={18} /></button>
                  <button onClick={() => stopRecording(false)} className="p-2 rounded-full text-white"
                          style={{ background: GREEN }} title={ar ? "إرسال" : "Send"}
                          data-testid="cs-rec-send"><StopCircle size={18} /></button>
                </div>
              ) : (
                <>
                  <button onClick={() => galleryRef.current?.click()} disabled={sendingMedia}
                          className="p-2.5 rounded-full opacity-70 hover:opacity-100 shrink-0" title={ar ? "صور من المعرض" : "Gallery"}
                          data-testid="cs-attach-btn">
                    <ImageIcon size={20} />
                  </button>
                  <button onClick={() => cameraRef.current?.click()} disabled={sendingMedia}
                          className="p-2.5 rounded-full opacity-70 hover:opacity-100 shrink-0" title={ar ? "التقاط صورة" : "Camera"}
                          data-testid="cs-camera-btn">
                    <Camera size={20} />
                  </button>
                  <input ref={galleryRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={pickFiles} />
                  <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickFiles} />
                  <textarea ref={taRef} value={input} rows={1}
                            onChange={(e) => onInputChange(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); }
                            }}
                            enterKeyHint="send"
                            placeholder={ar ? "اكتب رسالة…" : "Type a message…"}
                            className="flex-1 min-w-0 rounded-2xl px-4 py-2.5 text-sm outline-none bg-white resize-none"
                            style={{ border: "1px solid rgba(0,0,0,0.08)", fontSize: 16, maxHeight: 120, lineHeight: 1.45 }}
                            data-testid="cs-input" />
                  {(input.trim() || attachments.length) ? (
                    <button onClick={sendText} disabled={sendingMedia}
                            className="w-11 h-11 rounded-full flex items-center justify-center text-white shrink-0 disabled:opacity-50"
                            style={{ background: GREEN }} data-testid="cs-send-btn">
                      <Send size={18} className={ar ? "rtl-flip" : ""} />
                    </button>
                  ) : (
                    <button onClick={startRecording} disabled={sendingMedia}
                            className="w-11 h-11 rounded-full flex items-center justify-center text-white shrink-0"
                            style={{ background: "#11392A" }} data-testid="cs-mic-btn">
                      <Mic size={18} />
                    </button>
                  )}
                </>
              )}
            </div>
          </>
        ))}
      </div>

      {zoom && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
             onClick={() => setZoom(null)} data-testid="cs-zoom">
          <img src={zoom} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}

const Bubble = ({ mine, ar, children }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className="max-w-[82%] px-3 py-2 shadow-sm"
         style={{
           background: mine ? "#d9f3e6" : "#ffffff",
           borderRadius: mine
             ? (ar ? "14px 14px 14px 4px" : "14px 14px 4px 14px")
             : (ar ? "14px 14px 4px 14px" : "14px 14px 14px 4px"),
         }}>
      {children}
    </div>
  </div>
);
